<ng-select 
  class="erp-contact-selector"
  [items]="filteredContacts"
  bindLabel="name"
  [bindValue]="bindObject ? null! : 'contact_id'"
  [multiple]="multiple"
  [clearable]="clearable"
  [disabled]="isDisabled"
  [loading]="loading"
  [placeholder]="placeholder"
  [searchFn]="customSearchFn"
  [(ngModel)]="value"
  (change)="onSelectChange($event)"
  (clear)="onClear()"
  (open)="onDropdownOpen()"
  [appendTo]="appendToBody ? 'body' : null!"
>
  <!-- Dropdown Header Bar (Quick Filter Pills & Stats) -->
  @if (showHeaderFilter) {
    <ng-template ng-header-tmp>
      <div class="dropdown-header-bar d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
        <div class="d-flex align-items-center gap-1">
          <span class="text-muted fs-8 fw-semibold text-uppercase me-1">Status:</span>
          <button 
            type="button" 
            class="filter-pill-btn" 
            [class.active]="accountStatus === 'open'"
            (click)="setAccountStatusFilter('open', $event)">
            Active ({{ baseOpenCount }})
          </button>
          <button 
            type="button" 
            class="filter-pill-btn" 
            [class.active]="accountStatus === 'all'"
            (click)="setAccountStatusFilter('all', $event)">
            All ({{ baseTotalCount }})
          </button>
          <button 
            type="button" 
            class="filter-pill-btn" 
            [class.active]="accountStatus === 'closed'"
            (click)="setAccountStatusFilter('closed', $event)">
            Closed ({{ baseClosedCount }})
          </button>
        </div>

        @if (prioritizeRecent && recentContactIds.length > 0) {
          <div class="header-recent-badge" title="Recently opened contacts appear first">
            <i class="bi bi-clock-history me-1"></i>Recent First
          </div>
        }
      </div>
    </ng-template>
  }

  <!-- Selected Item / Label Template -->
  <ng-template ng-label-tmp let-item="item">
    <div class="selected-contact-chip d-flex align-items-center">
      <div 
        class="selected-avatar-mini me-2 flex-shrink-0" 
        [style.backgroundColor]="getAvatarColor(item?.name)">
        {{ getInitials(item?.name) }}
      </div>
      <div class="selected-text d-flex align-items-center gap-2 text-truncate">
        <span class="selected-name fw-semibold text-truncate">{{ item?.name }}</span>
        <span class="selected-id text-muted">#{{ item?.contact_id }}</span>
        @if (item?.contact_type?.contact_type_name) {
          <span class="selected-type-badge {{ item?.contact_type?.contact_type_code }}">
            {{ item?.contact_type?.contact_type_name }}
          </span>
        }
        @if (isRecent(item?.contact_id)) {
          <span class="selected-recent-badge" title="Recently opened contact">
            <i class="bi bi-clock-history text-primary"></i>
          </span>
        }
        @if (item?.account_status === 1) {
          <span class="selected-closed-badge">
            <i class="bi bi-lock-fill me-1"></i>Closed
          </span>
        }
      </div>
    </div>
  </ng-template>

  <!-- Dropdown Option Item Template -->
  <ng-template ng-option-tmp let-item="item" let-item$="item$" let-index="index">
    <div class="contact-option-row d-flex align-items-center" [class.is-closed]="item.account_status === 1">
      <!-- Left: Avatar Circle + Recent Indicator -->
      <div class="contact-avatar-wrapper position-relative me-3 flex-shrink-0">
        <div 
          class="contact-avatar-circle" 
          [style.backgroundColor]="getAvatarColor(item.name)">
          {{ getInitials(item.name) }}
        </div>
        @if (isRecent(item.contact_id)) {
          <span class="recent-indicator" title="Recently opened contact">
            <i class="bi bi-clock-history"></i>
          </span>
        }
      </div>

      <!-- Center: Main Contact Details -->
      <div class="contact-info flex-grow-1 min-w-0">
        <div class="d-flex align-items-center gap-2 mb-1 flex-wrap">
          <span class="contact-name fw-semibold text-truncate" [title]="item.name">
            {{ item.name }}
          </span>
          <span class="contact-id-badge">#{{ item.contact_id }}</span>

          @if (item.contact_type?.contact_type_name) {
            <span class="contact-type-badge {{ item.contact_type?.contact_type_code }}">
              {{ item.contact_type?.contact_type_name }}
            </span>
          }

          @if (isRecent(item.contact_id)) {
            <span class="badge-recent-pill" title="Recently opened contact">
              <i class="bi bi-clock-history me-1"></i>Recent
            </span>
          }

          @if (item.account_status === 1) {
            <span class="contact-status-badge closed" title="Account is closed">
              <i class="bi bi-lock-fill me-1"></i>Closed
            </span>
          }
        </div>

        <!-- Subtitle Details: Phone, City, GST -->
        <div class="contact-meta-row d-flex align-items-center gap-3 text-muted">
          @if (item.phone) {
            <span class="meta-item">
              <i class="bi bi-telephone text-success me-1"></i>{{ item.phone }}
            </span>
          }
          @if (item.billing_address?.address_city) {
            <span class="meta-item text-truncate">
              <i class="bi bi-geo-alt text-info me-1"></i>{{ item.billing_address.address_city }}
            </span>
          }
          @if (item.gst) {
            <span class="meta-item text-truncate d-none d-md-inline">
              <i class="bi bi-receipt text-secondary me-1"></i>GST: {{ item.gst }}
            </span>
          }
        </div>
      </div>

      <!-- Right: Financial Due / Balance & Selected Indicator -->
      <div class="contact-right-panel ms-2 d-flex align-items-center gap-2 flex-shrink-0">
        @if (showDueBalance && item.accounts) {
          @if (item.accounts.due > 0) {
            <div class="due-pill due-pending" title="Outstanding Due">
              <span class="due-label">Due</span>
              <span class="due-val">{{ item.accounts.due | currency:'INR':'symbol':'1.0-0' }}</span>
            </div>
          } @else if (item.accounts.due === 0 || item.accounts.due < 0) {
            <div class="due-pill due-settled" title="Account Settled">
              <i class="bi bi-check2-circle me-1"></i>Settled
            </div>
          }
        }

        @if (item$.selected) {
          <div class="selected-check-icon text-primary ms-1" title="Selected">
            <i class="bi bi-check-circle-fill fs-6"></i>
          </div>
        }
      </div>
    </div>
  </ng-template>

  <!-- Footer Template (Count & Quick Create Button) -->
  <ng-template ng-footer-tmp>
    <div class="dropdown-footer-bar d-flex align-items-center justify-content-between px-3 py-2 border-top">
      <span class="footer-hint text-muted fs-8">
        <i class="bi bi-info-circle me-1"></i>Showing <strong>{{ filteredContacts.length }}</strong> of {{ baseTotalCount }} contacts
      </span>
      @if (showQuickAdd) {
        <button 
          type="button" 
          class="quick-add-btn btn btn-sm d-inline-flex align-items-center"
          (click)="quickAddContact($event)">
          <i class="bi bi-plus-lg me-1"></i>New Contact
        </button>
      }
    </div>
  </ng-template>

  <!-- Empty / Not Found Template -->
  <ng-template ng-notfound-tmp let-searchTerm="searchTerm">
    <div class="not-found-container text-center py-4 px-3">
      <div class="not-found-icon-wrapper mb-2">
        <i class="bi bi-person-x fs-1 text-muted"></i>
      </div>
      <div class="fw-semibold text-dark fs-7 mb-1">No contacts found</div>
      <div class="text-muted fs-8 mb-3">
        @if (searchTerm) {
          No matching contact for <strong class="text-dark">"{{ searchTerm }}"</strong>
        } @else {
          No contacts match the current filter
        }
      </div>
      @if (showQuickAdd) {
        <button 
          type="button" 
          class="btn btn-sm btn-outline-primary d-inline-flex align-items-center"
          (click)="quickAddContact($event)">
          <i class="bi bi-person-plus me-1"></i>Create New Contact
        </button>
      }
    </div>
  </ng-template>

  <!-- Loading State Template -->
  <ng-template ng-loading-tmp>
    <div class="d-flex align-items-center justify-content-center py-3 text-muted">
      <div class="spinner-border spinner-border-sm text-primary me-2" role="status"></div>
      <span class="fs-8">Loading contacts...</span>
    </div>
  </ng-template>
</ng-select>
